<script lang="ts">
    // Bloco das coisas que acompanham o serviço: sala/equipamento bloqueado e insumos consumidos.
    // Fica recolhido em "Mais opções" quando nada disso está em jogo, e abre sozinho quando algum
    // serviço exige recurso ou quando a receita do serviço já lançou consumo.
    import ZentitySelect from '../../../_common/z/zentity-select.svelte'
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faBoxOpen, faCheck, faDoorClosed, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import type { Appointment } from '../../../../modules/schedulerApi/appointments/appointment'
    import { AppointmentResource } from '../../../../modules/schedulerApi/appointments/appointment-resource'
    import type { AppointmentConsumption } from '../../../../modules/schedulerApi/appointments/appointment-consumption'
    import type { ResourceLock } from '../../../../modules/schedulerApi/resources-lock/resource-lock'
    import AppointmentFormConsumption from '../../appointment-form-consumption.svelte'
    import ZsheetSection from '../../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetField from '../../../_common/z/sheet/zsheet-field.svelte'

    const dispatch = createEventDispatcher()

    export let entity: Appointment
    // ids ocupados por outros agendamentos no mesmo horário (calculado pelo cartão)
    export let lockedIds: string[] = []
    // algum serviço do agendamento exige bloqueio de sala/equipamento
    export let requiresResource: boolean = false

    $: resources = (entity.resources || []).filter((el) => !!el)
    $: consumptions = (entity.consumption || []).filter((el) => !!el)
    // aberto por obrigação (serviço exige) ou porque já tem conteúdo; senão, recolhido
    $: mustShow = requiresResource || resources.length > 0 || consumptions.length > 0
    let open = false
    let openTouched = false
    $: if (!openTouched) open = mustShow

    // ---- sala / equipamento ----
    let newResourceLock: ResourceLock = null
    let addingResource = false

    function startResource() {
        newResourceLock = null
        addingResource = true
        dispatch('refreshLocked')
    }

    function confirmResource() {
        if (!newResourceLock) {
            window.error('Selecione a sala ou equipamento')
            return
        }
        const resource = new AppointmentResource()
        resource.resourceLock = newResourceLock
        resource.resourcesLockId = newResourceLock.resourcesLockId
        entity.resources = [...(entity.resources || []), resource]
        addingResource = false
        newResourceLock = null
        dispatch('change')
    }

    function removeResource(resource: AppointmentResource) {
        entity.resources = (entity.resources || []).filter((el) => el !== resource)
        dispatch('change')
    }

    // ---- insumos ----
    // O editor do consumo continua sendo o do formulário atual (appointment-form-consumption.svelte):
    // é ele que carrega as regras de frasco/estoque. Aqui só desenhamos a linha fechada no padrão da
    // folha e o abrimos em modo de edição (startInEditMode) quando o atendente toca nela.
    let addingConsumption = false
    let editingConsumption: AppointmentConsumption = null

    function consumptionAdded(ev) {
        const consumption: AppointmentConsumption = ev.detail
        entity.consumption = [...(entity.consumption || []), consumption]
        addingConsumption = false
        dispatch('change')
    }

    // consumo que a receita do serviço lançou sozinha abre direto em edição: o "Eu estou" fica em
    // branco de propósito e precisa da confirmação do atendente (mesma regra do formulário atual)
    $: if (!editingConsumption) {
        const pending = consumptions.find((el) => el.startInEditMode)
        if (pending) editingConsumption = pending
    }

    function startEditConsumption(consumption: AppointmentConsumption) {
        consumption.startInEditMode = true
        editingConsumption = consumption
    }

    function finishEditConsumption() {
        if (editingConsumption) editingConsumption.startInEditMode = undefined
        editingConsumption = null
        entity.consumption = (entity.consumption || []).filter((el) => !!el)
        dispatch('change')
    }

    function removeConsumption(consumption: AppointmentConsumption) {
        if (editingConsumption === consumption) editingConsumption = null
        entity.consumption = (entity.consumption || []).filter((el) => el !== consumption)
        dispatch('change')
    }

    function consumptionLabel(consumption: AppointmentConsumption) {
        const parts = []
        if (consumption.quantity > 0) parts.push(`Qtd: ${consumption.quantity} ${consumption.service?.volumeUnit || ''}`.trim())
        if (consumption.openNew) parts.push('abriu frasco')
        if (consumption.closeAfter) parts.push('finalizou frasco')
        if (consumption.syncStatus === 'failed') parts.push('falha na sincronização')
        else if (consumption.syncStatus === 'pending' && consumption.applied === false) parts.push('baixa ao comparecer')
        return parts.join(' · ')
    }
</script>

<ZsheetSection
    label="Sala, equipamento e insumos"
    collapsible={!mustShow}
    bind:open
    on:toggle={() => (openTouched = true)}
    meta={!open && (resources.length > 0 || consumptions.length > 0) ? `${resources.length + consumptions.length}` : ''}
>
    <div class="extras">
        <!-- sala / equipamento -->
        <div class="extras_group">
            <div class="extras_title">Sala ou equipamento</div>
            {#if requiresResource && resources.length === 0}
                <div class="extras_warn">Um dos serviços escolhidos exige o bloqueio de uma sala ou equipamento.</div>
            {/if}
            {#each resources as resource}
                <div transition:slide={{ duration: 160 }}>
                    <ZsheetEntity
                        icon={faDoorClosed}
                        title={resource.resourceLock?.label || 'sala/equipamento'}
                        subtitle={lockedIds.includes(resource.resourcesLockId) ? 'em uso por outro agendamento neste horário' : ''}
                        removable={true}
                        removeTitle="Liberar"
                        on:remove={() => removeResource(resource)}
                    />
                </div>
            {/each}
            {#if addingResource}
                <div class="extras_edit" transition:slide={{ duration: 160 }}>
                    <ZsheetField label="Sala ou equipamento">
                        <ZentitySelect fonte="recurso" label="" params={{ ocupados: lockedIds }} bind:value={newResourceLock} />
                    </ZsheetField>
                    <div class="extras_acts">
                        <button type="button" class="act" on:click={() => (addingResource = false)}>Cancelar</button>
                        <div class="spacer"></div>
                        <button type="button" class="act act-primary" on:click={confirmResource}><Fa icon={faCheck} /> Bloquear</button>
                    </div>
                </div>
            {:else}
                <button type="button" class="add" on:click={startResource}><Fa icon={faPlus} /> Bloquear sala/equipamento</button>
            {/if}
        </div>

        <!-- insumos -->
        <div class="extras_group">
            <div class="extras_title">Insumos consumidos</div>
            {#each consumptions as consumption (consumption)}
                <div transition:slide={{ duration: 160 }}>
                    {#if editingConsumption === consumption}
                        <div class="extras_edit">
                            <AppointmentFormConsumption
                                items={entity.items}
                                appointmentConsumption={consumption}
                                on:cancel={() => removeConsumption(consumption)}
                                on:added={finishEditConsumption}
                            />
                        </div>
                    {:else}
                        <ZsheetEntity
                            icon={faBoxOpen}
                            title={consumption.service?.name || '-'}
                            subtitle={consumptionLabel(consumption)}
                            clickable={true}
                            removable={true}
                            removeTitle="Remover insumo"
                            on:click={() => startEditConsumption(consumption)}
                            on:remove={() => removeConsumption(consumption)}
                        />
                    {/if}
                </div>
            {/each}
            {#if addingConsumption}
                <div transition:slide={{ duration: 160 }}>
                    <AppointmentFormConsumption items={entity.items} on:cancel={() => (addingConsumption = false)} on:added={consumptionAdded} />
                </div>
            {:else}
                <button type="button" class="add" on:click={() => (addingConsumption = true)}><Fa icon={faPlus} /> Insumo</button>
            {/if}
        </div>
    </div>
</ZsheetSection>

<style>
    /* valores base = tema escuro */
    .extras {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
    .extras_group {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .extras_title {
        font-size: 12.5px;
        opacity: 0.55;
    }
    .extras_warn {
        font-size: 12.5px;
        color: #e8bd6a;
    }
    .extras_edit {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 10px;
        border-radius: 10px;
        background: #ffffff0a;
        border: 1px solid #2ecc7144;
    }
    .extras_acts {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .spacer {
        flex: 1 1 auto;
    }
    .add,
    .act {
        border: 1px dashed #ffffff33;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 14px;
        border-radius: 10px;
        padding: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 42px;
    }
    .act {
        border-style: solid;
        border-color: #ffffff22;
        padding: 8px 12px;
        min-height: 38px;
    }
    .act-primary {
        background: #2ecc71;
        border-color: #2ecc71;
        color: #10331f;
        font-weight: 700;
    }

    :global(.theme_light) .add,
    :global(.theme_light) .act {
        border-color: #d8dbe0;
    }
    :global(.theme_light) .extras_edit {
        background: #f7f8fa;
    }
    :global(.theme_light) .act-primary {
        color: #ffffff;
    }
    :global(.theme_light) .extras_warn {
        color: #9a6b12;
    }
</style>
